import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Portal } from '@chakra-ui/react';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import { COPY_ACTIVE_ATTR, COPY_ANCHOR_ATTR, COPY_FEEDBACK_Z_INDEX } from '../utils/tableCopySx';

interface CopyTooltipProps {
    /** True while we want to surface the post-action feedback (e.g. "Copied!"). */
    isCopied: boolean;
    /** Label shown when `isCopied` is true. Defaults to "Copied!". */
    copiedLabel?: string;
    /** Background of the bubble. Defaults to gray.900; pass red.500 for a failure state. */
    bg?: string;
    placement?: unknown;
    zIndex?: number;
    children: React.ReactElement;
}

/** Gap from the cursor — bubble appears above and to the right. */
const CURSOR_OFFSET_X = 12;
const CURSOR_OFFSET_Y = 8;
/** Keep the bubble at least this many pixels inside the viewport edge. */
const VIEWPORT_EDGE_MARGIN = 8;
const FADE_IN_MS = 160;
const FADE_OUT_MS = 450;

/**
 * Shows "Copied!" in a portalled bubble at the top-right of the cursor at
 * copy time. Children are not wrapped in a layout box, so triggers do not shift.
 */
const CopyTooltip: React.FC<CopyTooltipProps> = ({
    isCopied,
    copiedLabel = 'Copied!',
    bg = 'gray.900',
    children,
}) => {
    const text = isCopied ? copiedLabel : null;
    const { copiedKey: activeCopyKey, copyPosition } = useCopyToClipboard();

    const [stickyText, setStickyText] = useState<string | null>(null);
    const [stickyBg, setStickyBg] = useState(bg);
    const [bubblePos, setBubblePos] = useState<{ x: number; y: number } | null>(null);
    const [visible, setVisible] = useState(false);
    const bubbleNodeRef = useRef<HTMLDivElement | null>(null);
    const rafRef = useRef<number | null>(null);
    const fadeOutTimerRef = useRef<number | null>(null);
    const wasCopiedRef = useRef(false);

    useEffect(() => {
        if (isCopied) {
            wasCopiedRef.current = true;
        }

        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }

        if (text && copyPosition) {
            if (fadeOutTimerRef.current !== null) {
                window.clearTimeout(fadeOutTimerRef.current);
                fadeOutTimerRef.current = null;
            }
            setStickyText(text);
            setStickyBg(bg);
            setBubblePos({
                x: copyPosition.x + CURSOR_OFFSET_X,
                y: copyPosition.y,
            });
            rafRef.current = requestAnimationFrame(() => {
                setVisible(true);
                rafRef.current = null;
            });
        } else if (!text) {
            const preempted =
                wasCopiedRef.current &&
                !isCopied &&
                activeCopyKey !== null;

            if (preempted) {
                if (fadeOutTimerRef.current !== null) {
                    window.clearTimeout(fadeOutTimerRef.current);
                    fadeOutTimerRef.current = null;
                }
                setVisible(false);
                setStickyText(null);
                setBubblePos(null);
                wasCopiedRef.current = false;
            } else if (!isCopied && wasCopiedRef.current) {
                setVisible(false);
                wasCopiedRef.current = false;
                if (fadeOutTimerRef.current === null) {
                    fadeOutTimerRef.current = window.setTimeout(() => {
                        setStickyText(null);
                        setBubblePos(null);
                        fadeOutTimerRef.current = null;
                    }, FADE_OUT_MS);
                }
            }
        }

        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [text, bg, isCopied, activeCopyKey, copyPosition]);

    useEffect(() => () => {
        if (fadeOutTimerRef.current !== null) {
            window.clearTimeout(fadeOutTimerRef.current);
        }
    }, []);

    /**
     * Imperatively clamps the bubble's `left` after mount so it never spills
     * past the viewport edge — critical for copy triggers near the right side
     * of the screen (e.g. inside right-anchored drawers). We mutate the DOM
     * directly instead of state so the position is corrected synchronously
     * the moment the node attaches, with no render-cycle flicker.
     */
    const clampBubblePosition = useCallback(() => {
        const node = bubbleNodeRef.current;
        if (!node || typeof window === 'undefined' || bubblePos === null) return;
        const rect = node.getBoundingClientRect();
        const maxLeft = window.innerWidth - rect.width - VIEWPORT_EDGE_MARGIN;
        const clampedX = Math.max(
            VIEWPORT_EDGE_MARGIN,
            Math.min(bubblePos.x, maxLeft),
        );
        node.style.left = `${clampedX}px`;
    }, [bubblePos]);

    /**
     * Callback ref so positioning runs the instant React attaches the node to
     * the DOM (before the browser has a chance to paint the unclamped position).
     */
    const bubbleRefCallback = useCallback((node: HTMLDivElement | null) => {
        bubbleNodeRef.current = node;
        if (node) clampBubblePosition();
    }, [clampBubblePosition]);

    /**
     * Re-clamp on viewport resize while a bubble is visible — keeps the
     * "Copied!" feedback on-screen if the user resizes mid-fade.
     */
    useEffect(() => {
        if (!bubblePos || typeof window === 'undefined') return;
        const handleResize = () => clampBubblePosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [bubblePos, clampBubblePosition]);

    return (
        <>
            {children}
            {/* Zero-size marker for table overflow/scroll hooks — does not affect layout */}
            <Box
                as="span"
                w={0}
                h={0}
                overflow="hidden"
                display="inline-block"
                verticalAlign="middle"
                aria-hidden
                pointerEvents="none"
                {...{ [COPY_ANCHOR_ATTR]: '' }}
                {...(stickyText ? { [COPY_ACTIVE_ATTR]: '' } : {})}
            />
            {stickyText && bubblePos && (
                <Portal>
                    <Box
                        ref={bubbleRefCallback}
                        position="fixed"
                        left={`${bubblePos.x}px`}
                        top={`${bubblePos.y - CURSOR_OFFSET_Y}px`}
                        transform="translateY(-100%)"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={stickyBg}
                        color="white"
                        fontSize="xs"
                        fontWeight="medium"
                        lineHeight={1}
                        borderRadius="lg"
                        px={3}
                        py={2}
                        whiteSpace="nowrap"
                        boxShadow="none"
                        pointerEvents="none"
                        zIndex={COPY_FEEDBACK_Z_INDEX}
                        opacity={visible ? 1 : 0}
                        transition={visible
                            ? `opacity ${FADE_IN_MS}ms ease-out`
                            : `opacity ${FADE_OUT_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`}
                    >
                        {stickyText}
                    </Box>
                </Portal>
            )}
        </>
    );
};

export default CopyTooltip;
